import { createReducer } from '@reduxjs/toolkit';
import { Reducer } from 'redux';

import {
    decreaseTicketCounter,
    EventPaymentActions,
    EventPaymentsState,
    increaseTicketCounter,
    resetSelectedTickets,
    selectSeat,
    selectTicket,
} from '@/store/event-payment/event-payment.actions';
import { TicketSelectOption } from '@/types/tickets';

const initialState: EventPaymentsState = {
    selectedTickets: [],
};

export const eventPaymentReducer: Reducer<EventPaymentsState, EventPaymentActions> = createReducer(
    initialState,
    (builder) => {
        builder
            .addCase(
                increaseTicketCounter,
                (state: EventPaymentsState, action: ReturnType<typeof increaseTicketCounter>) => {
                    return Object.assign<object, EventPaymentsState, Partial<EventPaymentsState>>({}, state, {
                        selectedTickets: setTicketQuantity(state.selectedTickets, action.payload, 'increase'),
                    });
                },
            )
            .addCase(
                decreaseTicketCounter,
                (state: EventPaymentsState, action: ReturnType<typeof decreaseTicketCounter>) => {
                    return Object.assign<object, EventPaymentsState, Partial<EventPaymentsState>>({}, state, {
                        selectedTickets: setTicketQuantity(state.selectedTickets, action.payload, 'decrease'),
                    });
                },
            )
            .addCase(selectTicket, (state: EventPaymentsState, action: ReturnType<typeof selectTicket>) => {
                return Object.assign<object, EventPaymentsState, Partial<EventPaymentsState>>({}, state, {
                    selectedTickets: setTicketQuantity(
                        state.selectedTickets,
                        action.payload.ticket,
                        action.payload.action,
                    ),
                });
            })
            .addCase(selectSeat, (state: EventPaymentsState, action: ReturnType<typeof selectSeat>) => {
                const newTickets = state.selectedTickets.some((ticket) => ticket?.tag === action.payload.ticket.tag)
                    ? state.selectedTickets.filter((ticket) => ticket?.tag !== action.payload.ticket.tag)
                    : [...state.selectedTickets, action.payload.ticket];
                return Object.assign<object, EventPaymentsState, Partial<EventPaymentsState>>({}, state, {
                    selectedTickets: newTickets,
                });
            })
            .addCase(resetSelectedTickets, (state: EventPaymentsState) =>
                Object.assign<object, EventPaymentsState, Partial<EventPaymentsState>>({}, state, {
                    selectedTickets: [],
                }),
            );
    },
);

function setTicketQuantity(
    storedTickets: TicketSelectOption[],
    newTicket: TicketSelectOption,
    action: 'increase' | 'decrease',
) {
    const existingTicketIndex = storedTickets.findIndex((ticket) => ticket.tag === newTicket.tag);

    if (action === 'increase') {
        return existingTicketIndex !== -1
            ? storedTickets.map((ticket, index) =>
                  index === existingTicketIndex ? { ...ticket, quantity: (ticket.quantity || 1) + 1 } : ticket,
              )
            : [...storedTickets, { ...newTicket, quantity: 1 }];
    }

    if (action === 'decrease') {
        return storedTickets
            .map((ticket, index) => {
                if (index === existingTicketIndex) {
                    const newQuantity = (ticket.quantity || 1) - 1;
                    return { ...ticket, quantity: newQuantity };
                }
                return ticket;
            })
            .filter((ticket) => ticket.quantity > 0);
    }
}
